import { AlertTriangle, ShieldAlert, Copy, Gavel } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { ruleDisplayName } from '@/lib/moderationPipeline';

export interface ModerationFlagMessageProps {
  ruleId: string;
  severity: 'advisory' | 'violation';
  humanReadable: string;
  correlationId: string;
  /** Event-id från DB — används för att länka eventuell appeal. */
  eventId?: string;
  /** Callback som öppnar SubmitAppealDialog med event + rule-context. */
  onAppeal?: (args: { eventId?: string; ruleId: string; correlationId: string }) => void;
  /** Extra metadata-rad (t.ex. "Strike 2/3 — sista varningen"). */
  contextLine?: string;
}

/**
 * Chat-bubble som visas i stället för AI-svar när en prompt flaggas.
 * Ersätter det gamla generic EXFIL_REFUSAL-meddelandet med strukturerad
 * info: vilken regel, severity, correlation-id (copy-to-clipboard),
 * appeal-knapp.
 */
export function ModerationFlagMessage({
  ruleId,
  severity,
  humanReadable,
  correlationId,
  eventId,
  onAppeal,
  contextLine,
}: ModerationFlagMessageProps) {
  const isViolation = severity === 'violation';
  const Icon = isViolation ? ShieldAlert : AlertTriangle;
  const toneClass = isViolation
    ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
    : 'border-amber-400 bg-amber-50 dark:bg-amber-950/20';
  const iconClass = isViolation
    ? 'text-red-600 dark:text-red-400'
    : 'text-amber-600 dark:text-amber-400';

  const copyCorrelation = () => {
    navigator.clipboard.writeText(correlationId).then(() =>
      toast({ title: 'Correlation-ID kopierat', description: correlationId }),
    );
  };

  return (
    <div
      className={`border rounded-lg p-3 my-2 space-y-2 ${toneClass}`}
      role="alert"
      data-testid="moderation-flag-message"
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconClass}`} aria-hidden="true" />
        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold">
              {isViolation ? 'Meddelandet blockerades' : 'Notering på meddelandet'}
            </div>
            <Badge
              variant="outline"
              className={
                isViolation
                  ? 'border-red-400 text-red-700 dark:text-red-300'
                  : 'border-amber-400 text-amber-700 dark:text-amber-300'
              }
            >
              {isViolation ? 'violation • räknas' : 'advisory • räknas inte'}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground">
            Regel: <strong>{ruleDisplayName(ruleId)}</strong>
          </div>
          <p className="text-sm">{humanReadable}</p>
          {contextLine && (
            <div className="text-xs text-muted-foreground italic">{contextLine}</div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-current/10">
        <button
          type="button"
          onClick={copyCorrelation}
          className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-foreground"
          title="Kopiera correlation-ID"
        >
          <Copy className="w-3 h-3" />
          {correlationId}
        </button>
        {isViolation && onAppeal && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1 h-7"
            onClick={() => onAppeal({ eventId, ruleId, correlationId })}
          >
            <Gavel className="w-3.5 h-3.5" />
            Överklaga detta
          </Button>
        )}
      </div>
    </div>
  );
}
